import React, { useEffect, useMemo, useReducer, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary } from 'react-error-boundary'

import { initStateObject, objectReducer } from 'reducers/default'

import editEvent from 'images/icons/edit.svg'
import { upperCase } from 'lodash'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import EventHeader from 'components/Event/Details/EventHeader'
import CampaignActivationModal from '../CampaignStatusModals/CampaignActivationModal'
import CampaignOptimizationModal from '../CampaignStatusModals/CampaignOptimizationModal'
import { EventCampaigns } from '../EventCampaigns'
import AutosaveStatus from './Edit/AutosaveStatus'
import { changeFbCampaignStatus, changeOptimizationStatus } from 'api/events'
import { EventStatsProvider } from 'components/Event/Details/EventStatsProvider'
import CampaignSetupProvider from './Edit/CampaignSetupProvider'
import { EventCampaignStatus } from 'api/models'
import ErrorFallback from 'components/Shared/ErrorFallback'
import ErrorMessage from 'components/Shared/ErrorMessage'
import PortalModal from 'components/Shared/PortalModal'

export default function Recommendation({ event }) {
    const { t } = useTranslation()
    const history = useHistory()
    const [campaignStatus, statusDispatch] = useReducer(objectReducer, initStateObject)
    const [isCampaignActive, setIsCampaignActive] = useState(false)
    const [isCampaignOptimized, setIsCampaignOptimized] = useState(false)
    const [isOptimizationModalOpen, setIsOptimizationModalOpen] = useState(false)
    const [campaignOptimized, optimizationDispatch] = useReducer(objectReducer, initStateObject)
    const [isActivationModalOpen, setIsActivationModalOpen] = useState(false)

    useEffect(() => {
        if (campaignStatus.error) setIsCampaignActive(false)
        if (campaignOptimized.error) setIsCampaignOptimized(false)
    }, [campaignOptimized, campaignStatus])

    useEffect(() => {
        if (event) {
            const isCampaignActive =
                upperCase(event?.client_campaign_status) === EventCampaignStatus.active ||
                (event?.client_campaign_status === null && event?.has_active_campaigns)
            const isCampaignOptimized = upperCase(event?.campaign_optimisation_status) !== EventCampaignStatus.paused
            setIsCampaignActive(isCampaignActive)
            setIsCampaignOptimized(isCampaignOptimized)
        }
    }, [event])

    const showCampaignOptions = useMemo(() => {
        if (event?.client_campaign_status)
            return Object.values(EventCampaignStatus).includes(upperCase(event?.client_campaign_status))
        return event?.has_active_campaigns
    }, [event])
    const isPastEvent = useMemo(() => moment(event?.date) < moment(), [event])

    const menuOptions = [
        {
            title: t('Event.edit'),
            clickHandler: () => history.replace(`/events/edit/${event.id}`),
            optionType: 'button',
            icon: editEvent,
        },
    ]

    if (showCampaignOptions) {
        const extraOptions = {
            title: isPastEvent
                ? t('Recommendations.campaignOptions.statuses.inactive')
                : isCampaignActive
                    ? t('Recommendations.campaignOptions.statuses.active')
                    : t('Recommendations.campaignOptions.statuses.paused'),
            optionType: 'expandableItem',
            entries: [
                {
                    title: t('Recommendations.campaignOptions.activateCampaign'),
                    optionType: 'switch',
                    onClick: () => setIsActivationModalOpen(true),
                    checked: isCampaignActive,
                    disabled: isPastEvent,
                },
            ],
        }

        if (isCampaignActive) {
            extraOptions.entries.push({
                title: t('Recommendations.campaignOptions.optimizeCampaign'),
                optionType: 'switch',
                onClick: () => setIsOptimizationModalOpen(true),
                checked: isCampaignOptimized,
                disabled: isPastEvent,
            })
        }

        menuOptions.push(extraOptions)
    }

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <EventStatsProvider eventId={event.id}>
                <CampaignSetupProvider eventId={event.id}>
                    <>
                        <EventHeader
                            menuOptions={menuOptions}
                            classes="event-header_recommendation-drawer-padding"
                            event={event}
                            showActiveBadge={false}
                            showMenu={true}
                        >
                            <AutosaveStatus />
                        </EventHeader>
                        {campaignStatus.error && (
                            <ErrorMessage classNames="recommendations-page_error" danger>
                                {t(campaignStatus.message)}
                            </ErrorMessage>
                        )}
                        {campaignOptimized.error && (
                            <ErrorMessage classNames="recommendations-page_error" danger>
                                {t(campaignOptimized.message)}
                            </ErrorMessage>
                        )}
                        <EventCampaigns eventId={event.id} />
                        <PortalModal isOpen={isActivationModalOpen}>
                            <CampaignActivationModal
                                status={isCampaignActive ? 'pause' : 'activate'}
                                onCancel={() => setIsActivationModalOpen(false)}
                                onConfirm={() => {
                                    setIsActivationModalOpen(false)
                                    const status = isCampaignActive
                                        ? EventCampaignStatus.paused
                                        : EventCampaignStatus.active
                                    changeFbCampaignStatus(statusDispatch, event.id, status)
                                    setIsCampaignActive((prev) => !prev)
                                }}
                            />
                        </PortalModal>
                        <PortalModal isOpen={isOptimizationModalOpen}>
                            <CampaignOptimizationModal
                                status={isCampaignOptimized ? 'deactivate' : 'activate'}
                                onCancel={() => setIsOptimizationModalOpen(false)}
                                onConfirm={() => {
                                    setIsOptimizationModalOpen(false)
                                    const status = isCampaignOptimized
                                        ? EventCampaignStatus.paused
                                        : EventCampaignStatus.active
                                    changeOptimizationStatus(optimizationDispatch, event.id, status)
                                    setIsCampaignOptimized((prev) => !prev)
                                }}
                            />
                        </PortalModal>
                    </>
                </CampaignSetupProvider>
            </EventStatsProvider>
        </ErrorBoundary>
    )
}

Recommendation.propTypes = {
    event: PropTypes.object.isRequired,
}
